import {
  Component,
  createContext,
  lazy,
  Suspense,
  useContext,
  type ComponentType,
  type ReactNode,
} from "react";

export const DeferredRecoveryContext = createContext(false);

class FeatureBoundary extends Component<
  { children: ReactNode; label: string; busy: boolean },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed)
      return (
        <div className="feature-recovery" role="alert">
          <p>
            {this.props.label} could not open. Check your connection, then
            reload when your take is finished.
          </p>
          <p>Your script and live prompting are still available.</p>
          <button data-help={this.props.busy ? "Pause prompting and stop audio activity before reloading this optional feature." : `Reload the app to try opening ${this.props.label.toLowerCase()} again.`}
            className="subtle-button"
            disabled={this.props.busy}
            onClick={() => location.reload()}
          >
            Reload to reopen {this.props.label.toLowerCase()}
          </button>
          {this.props.busy && (
            <p>Pause prompting and stop audio activity to reload safely.</p>
          )}
        </div>
      );
    return this.props.children;
  }
}

/** A failed optional chunk must never tear down the live room or its transport. */
export function deferredFeature<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
  label: string,
) {
  const InitialContent = lazy(load);
  return function DeferredFeature(props: P) {
    const busy = useContext(DeferredRecoveryContext);
    const Content = InitialContent;
    return (
      <FeatureBoundary label={label} busy={busy}>
        <Suspense
          fallback={
            <p className="setting-help" role="status">
              Opening {label.toLowerCase()}…
            </p>
          }
        >
          <Content {...props} />
        </Suspense>
      </FeatureBoundary>
    );
  };
}
